import { useRef, useCallback, useState } from 'react';
import { IconExternalLink, IconCode } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import postRobot from 'post-robot';
import * as Sentry from '@sentry/react';
import mergeWith from 'lodash.mergewith';

import { STATUS_STATES } from '../../core/constants';
import Button from '../buttons/Button';
import { useAccount } from '../../store/account/hooks';
import appSettings from '../../app-settings';
import PageEditor from '../page-editor/PageEditor';
import StepNavBottom from './StepNavBottom';
import AlertMessage from '../alerts/AlertMessage';
import { getNetworkError, getPageUrl, waitFor } from '../../core/utils';
import { getPage, upsertPage } from '../../api';
import EmbedModal from '../donation-page/EmbedModal';

const DonationPageStep = ({ onPrev, onNext }) => {
  const editorRef = useRef(null);
  const { account, getAccount } = useAccount();
  const [status, setStatus] = useState({ state: STATUS_STATES.idle, message: '' });
  const [pageData, setPageData] = useState(null);
  const [embedModalOpen, setEmbedModalOpen] = useState(false);

  const slug = 'default';

  const getPageData = useCallback(async () => {
    try {
      setStatus({ state: STATUS_STATES.loading, message: '' });
      const data = await getPage(slug);
      setPageData(data || null);
      setStatus({
        state: STATUS_STATES.idle,
        message: '',
      });
      return data || null;
    } catch (error) {
      Sentry.captureException(error);
      setStatus({ state: STATUS_STATES.error, message: getNetworkError(error) });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Eagerly kick off the initial fetch during render (not in useEffect) so that
  // pageDataPromiseRef is guaranteed to be populated before the iframe's onLoad
  // can fire. useEffect runs *after* the browser paints, so a cached / fast
  // iframe could trigger handleEditorLoad while the ref is still null.
  const pageDataPromiseRef = useRef(null);
  if (pageDataPromiseRef.current === null) {
    pageDataPromiseRef.current = getPageData();
  }

  const pageUrl = getPageUrl({ account, slug });

  const handleEditorLoad = async () => {
    try {
      // Wait for page data to be fetched before initializing the editor.
      // This prevents a race condition where the iframe loads before the
      // API call resolves, which would cause the editor to miss saved content.
      const resolvedPageData = await pageDataPromiseRef.current;

      const baseUrl = pageUrl.replace('slug', '');

      const content = resolvedPageData?.content
        ? {
            ...resolvedPageData.content,
            metadata: { name: resolvedPageData.name || '', slug, baseUrl },
          }
        : { metadata: { baseUrl, name: 'Default Donation Page', slug } };

      const initialState = account?.logoUrl
        ? mergeWith({}, { images: { headerImage: { url: account.logoUrl } } }, content)
        : content;

      await postRobot.send(editorRef.current.contentWindow, 'initEditor', {
        initialState,
        isOnboarding: true,
        tenantId: account?.tenantId,
      });
    } catch (error) {
      Sentry.captureException(error);
      setStatus({ state: STATUS_STATES.error, message: 'Something went wrong while initializing the editor.' });
    }
  };

  const handleSave = async () => {
    try {
      if (!account) return;

      const response = await postRobot.send(editorRef.current.contentWindow, 'saveAndExit');

      if (!response.data) return;

      setStatus({ state: STATUS_STATES.loading, message: '' });

      const pageSlug = slug || response.data.metadata.slug;

      await upsertPage({
        tenantId: account.tenantId,
        slug: pageSlug,
        content: response.data,
        action: pageData ? 'update' : 'create',
      });

      if (!account.donationPageSubdomain) getAccount();

      // Empiric value - page needs some extra time to reflect changes
      await waitFor(2000);

      // Re-fetch page state for reflecting updated state on the editor
      await getPageData();
      setStatus({ state: STATUS_STATES.success, message: 'Donation page updated successfully!' });
    } catch (err) {
      Sentry.captureException(err);
      setStatus({ state: STATUS_STATES.error, message: getNetworkError(err) });
    }
  };

  const loading = status.state === STATUS_STATES.loading;
  const error = status.state === STATUS_STATES.error;
  const success = status.state === STATUS_STATES.success;

  return (
    <div className="m-auto flex h-full max-w-5xl flex-col space-y-2 px-4 pb-4">
      <div className="space-y-4">
        <div className="flex justify-between space-x-3">
          <h1 className="text-h1 font-semibold">Set up a donation page</h1>

          <div className="mt-1 flex items-center space-x-2">
            <Button title="Save" color="primary" onClick={handleSave} size="sm" />

            <Button
              title="Preview"
              href={pageUrl}
              target="_blank"
              rel="noreferrer"
              LeftIcon={IconExternalLink}
              size="sm"
              disabled={!account?.donationPageSubdomain}
            />

            <Button
              title="Embed"
              onClick={() => setEmbedModalOpen(true)}
              LeftIcon={IconCode}
              size="sm"
              disabled={!account?.donationPageSubdomain}
            />
          </div>
        </div>
        <p className="text-lg">Create your first donation page.</p>
      </div>

      <div className="flex-1 min-[1472px]:-ml-10">
        <PageEditor
          ref={editorRef}
          pageUrl={`${appSettings.donationPageEditorUrl}/?customerSupportEmail=${account?.customerSupportEmail}`}
          loading={loading}
          onLoad={handleEditorLoad}
        />
      </div>

      <StepNavBottom
        // Automatically save when navigating back and forth to avoid losing changes
        onPrev={async () => {
          await handleSave();
          await waitFor(2000); // Give some time for success alert to be seen
          onPrev();
        }}
        onNext={async () => {
          await handleSave();
          await waitFor(2000); // Give some time for success alert to be seen
          onNext();
        }}
        prevDisabled={loading}
        nextDisabled={loading || !account?.donationPageSubdomain}
      />

      <EmbedModal open={embedModalOpen} onClose={() => setEmbedModalOpen(false)} />

      <AlertMessage
        open={success}
        message={status.message}
        onClose={() => setStatus({ state: STATUS_STATES.idle, message: '' })}
        severity="success"
      />

      <AlertMessage
        open={error}
        message={status.message}
        onClose={() => setStatus({ state: STATUS_STATES.idle, message: '' })}
        severity="error"
      />
    </div>
  );
};

DonationPageStep.propTypes = {
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default DonationPageStep;
